<template>
  <aside
    class="fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-xl transform transition-all duration-300 ease-in-out lg:translate-x-0"
    :class="[
      { '-translate-x-full': !isOpen, 'translate-x-0': isOpen },
      { 'w-64': !isCollapsed, 'w-64 lg:w-20': isCollapsed }
    ]"
  >
    <div class="flex flex-col h-full">
      <!-- Logo Section -->
      <div
        class="logo-section flex items-center justify-center h-16"
      >
        <div
          class="flex items-center justify-center px-2 w-full transition-all duration-300"
        >
          <!-- Collapsed: Logo Image -->
          <div v-if="isCollapsed" class="hidden lg:flex items-center justify-center">
            <img :src="collapsedLogoImage" alt="Company Logo" class="h-[3.5rem] w-auto" :style="isDarkMode ? 'filter: drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff);' : ''" />
          </div>
          <!-- Expanded: Logo Image -->
          <div v-else class="flex items-center gap-2">
            <img :src="logoImage" alt="Company Logo" class="h-[4rem] w-auto" :style="isDarkMode ? 'filter: drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff) drop-shadow(0 0 .5px #fff);' : ''" />
            <span
              class="text-[2rem] font-extrabold tracking-tight text-primary dark:text-primary-dark-text"
            >
            </span>
          </div>
        </div>
      </div>

      <!-- Navigation Section -->
      <nav class="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        <router-link
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          class="group flex items-center py-2.5 rounded-xl transition-all duration-200 relative overflow-hidden"
          :class="[
            {
              'bg-[#0b6623]/10 dark:bg-emerald-950/40 text-[#0b6623] dark:text-emerald-300 font-bold':
                isActive(item.to),
              'text-gray-700 dark:text-gray-200 hover:bg-[#eefaf2] hover:text-[#0b6623] dark:hover:bg-gray-700/50': !isActive(item.to),
              'lg-tooltip': isCollapsed
            },
            isCollapsed ? 'px-2 lg:justify-center' : 'px-3',
            isCollapsed ? 'justify-start lg:justify-center' : 'justify-start'
          ]"
          :title="isCollapsed ? item.name : ''"
        >
          <!-- Active Indicator: Golden Yellow Bar matching the website nav indicator -->
          <div
            v-if="isActive(item.to)"
            class="absolute left-0 top-0 bottom-0 w-1.5 bg-[#ffd62e] rounded-r-full shadow-xs"
          ></div>

          <!-- Navigation Item Content -->
          <div
            class="flex items-center transition-all duration-200 relative z-10"
            :class="{
              'lg:justify-center space-x-3 lg:space-x-0': isCollapsed,
              'space-x-3': !isCollapsed
            }"
          >
            <div
              class="flex items-center justify-center rounded-lg transition-all duration-200"
              :class="[
                {
                  'bg-[#0b6623]/15 text-[#0b6623] dark:bg-emerald-900/50 dark:text-emerald-300': isActive(item.to),
                  'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 group-hover:bg-[#0b6623]/10 group-hover:text-[#0b6623] dark:group-hover:text-emerald-300': !isActive(item.to)
                },
                isCollapsed ? 'w-10 h-10' : 'w-8 h-8'
              ]"
            >
              <component
                :is="item.icon"
                :class="[
                  'w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:scale-110',
                  isActive(item.to)
                    ? 'text-[#0b6623] dark:text-emerald-300'
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-[#0b6623] dark:group-hover:text-emerald-300'
                ]"
              />
            </div>
            <span
              class="text-sm font-bold truncate transition-all duration-200"
              :class="{
                'lg:hidden': isCollapsed,
                block: !isCollapsed
              }"
            >
              {{ item.name }}
            </span>
          </div>

          <!-- Hover Effect -->
          <div
            class="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[#0b6623]/5 dark:bg-emerald-400/5 transition-all duration-300"
            :class="{ 'opacity-0': isActive(item.to) }"
          ></div>
        </router-link>
      </nav>

      <!-- Footer Section -->
      <div
        v-if="!isCollapsed"
        class="p-4 border-t border-gray-100 dark:border-gray-700/80 transition-all duration-300 bg-gray-50/60 dark:bg-gray-800/60"
      >
        <div class="text-center text-[11px] text-gray-500 dark:text-gray-400 space-y-1">
          <p class="font-bold text-[#0b6623] dark:text-emerald-400">Jeducare Services, Inc.</p>
          <div class="flex items-center justify-center space-x-1.5 text-[10px] text-gray-400">
            <span>Bowie &bull; Clinton</span>
            <span>&bull;</span>
            <span>© {{ new Date().getFullYear() }}</span>
          </div>
        </div>
      </div>
    </div>
  </aside>

  <!-- Mobile Overlay -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
    @click="toggleSidebar"
  ></div>
</template>

<script setup lang="ts">
// Imports
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import logoImage from '@/assets/company-logo.png'
import collapsedLogoImage from '@/assets/collapsed-logo.png'
import { getNavigationItems } from '@/router'
import { useUser } from '@/composables/auth/useUser'
import { useThemeStore } from '@/stores/theme'

// Props & Emits
const props = defineProps<{
  isOpen: boolean
  isCollapsed: boolean
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'update:isCollapsed', value: boolean): void
}>()

// Composables
const route = useRoute()
const router = useRouter()
const { userType, isOwner } = useUser()
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// Computed Properties
const navigation = computed(() => getNavigationItems([...router.options.routes], userType.value, isOwner.value))

// Methods
const isActive = (path: string) => route.path === path
const toggleSidebar = () => emit('update:isOpen', !props.isOpen)
</script>

<style scoped>
/* Enhanced transition animations */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Smooth width transitions */
aside {
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced hover effects for collapsed sidebar */
.group:hover {
  transform: translateY(-1px);
}

/* Icon scaling animation */
.group-hover\:scale-110:hover {
  transform: scale(1.1);
}

/* Tooltip enhancement - only show on desktop */
.lg-tooltip:hover::after {
  position: absolute;
  z-index: 1000;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: #1f2937;
  color: white;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  content: attr(title);
  opacity: 0.9;
  pointer-events: none;
}

@media (max-width: 1024px) {
  .lg-tooltip:hover::after {
    display: none;
  }
}

</style>
